<template>
  <v-dialog v-model="show" max-width="1200px">
    <v-card style="max-height: 85vh; overflow-y: auto;">
      <block v-if="loading"></block>
      
      <v-card-title class="headline grey darken-3 white--text">
        Detalles de Factura - Pendiente de Entrega
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Información de la Factura -->
        <v-row class="my-2">
          <v-col cols="12" md="6">
            <div class="info-section">
              <h3>Información General</h3>
              <p><strong>ID Factura:</strong> {{ invoice.ID_Invoice }}</p>
              <p><strong>Fecha:</strong> {{ invoice.InvoiceDate }}</p>
              <p><strong>Tipo:</strong> {{ invoice.InvoiceType }}</p>
              <p><strong>Cliente:</strong> {{ invoice.InvoiceName }}</p>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="info-section">
              <h3>Totales</h3>
              <p><strong>Subtotal:</strong> ${{ formatNumber(invoice.InvoiceSubTotal) }}</p>
              <p><strong>IVA:</strong> ${{ formatNumber(invoice.InvoiceTotalIva) }}</p>
              <p><strong>Total:</strong> ${{ formatNumber(invoice.InvoiceTotalPagar) }}</p>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Tabla de Productos -->
        <h3 class="mb-3">Productos de la Factura</h3>
        <v-data-table
          :headers="headers"
          :items="invoiceItems"
          :items-per-page="-1"
          class="elevation-1 table-scrollable"
          :loading="loading"
          hide-default-footer
          fixed-header
          max-height="350px"
        >
          <template v-slot:item.ItemQuantity="{ item }">
            {{ item.ItemQuantity }}
          </template>

          <template v-slot:item.ItemUnitPrice="{ item }">
            ${{ formatNumber(item.ItemUnitPrice) }}
          </template>

          <template v-slot:item.ItemTotal="{ item }">
            ${{ formatNumber(item.ItemGravada + item.ItemExenta + item.ItemNoSuj) }}
          </template>

          <template v-slot:item.WarehouseName="{ item }">
            {{ item.WarehouseName || 'N/A' }}
          </template>

          <template v-slot:item.IsDelivered="{ item }">
            <v-chip
              :color="getStatusColor(item)"
              small
              text-color="white"
            >
              {{ getStatusText(item) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="!item.IsDelivered && canBeDelivered(item)"
              color="primary"
              small
              @click="markAsDelivered(item)"
              :disabled="markingDelivery"
            >
              <v-icon small left>mdi-check</v-icon>
              Marcar Entregado
            </v-btn>
            <v-tooltip v-else-if="!item.IsDelivered && !canBeDelivered(item)" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="grey"
                  small
                  disabled
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small left>mdi-close-circle</v-icon>
                  No disponible
                </v-btn>
              </template>
              <span>{{ getDisabledReason(item) }}</span>
            </v-tooltip>
            <span v-else class="text--secondary">
              {{ formatDate(item.DeliveryDate) }}
            </span>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="closeDialog">
          Cerrar
        </v-btn>
        <v-btn
          color="primary"
          @click="imprimirFacturaDirecta"
          :disabled="loading"
        >
          <v-icon left>mdi-printer</v-icon>
          Imprimir Factura
        </v-btn>
        <v-btn
          v-if="hasPendingItems"
          color="success"
          @click="markAllAsDelivered"
          :disabled="markingDelivery"
        >
          <v-icon left>mdi-check-all</v-icon>
          Marcar Todos Entregados
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Alert Component -->
    <Alerts
      :type="alert.type"
      :show="alert.show"
      :header="alert.header"
      :body="alert.body"
      @acceptAlert="acceptAlert"
      @closeAlert="closeAlert"
    />
  </v-dialog>
</template>

<script>
import Block from "@/components/Block";
import Alerts from "@/components/Alerts";
import { imprimirFactura } from "../../utils/printInvoice";

export default {
  name: "PendingDeliveryDetails",
  components: {
    Block,
    Alerts
  },
  props: {
    invoiceId: {
      type: String,
      default: null
    },
    filteredWarehouse: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      show: false,
      loading: false,
      markingDelivery: false,
      invoice: {},
      invoiceItems: [],
      headers: [
        { text: "Código", value: "ID_Item", width: "120px" },
        { text: "Descripción", value: "ItemDescription", width: "250px" },
        { text: "Cantidad", value: "ItemQuantity", width: "100px", align: "center" },
        { text: "Precio Unit.", value: "ItemUnitPrice", width: "120px", align: "right" },
        { text: "Total", value: "ItemTotal", width: "120px", align: "right" },
        { text: "Bodega", value: "WarehouseName", width: "150px" },
        { text: "Estado", value: "IsDelivered", width: "120px", align: "center" },
        { text: "Acciones", value: "actions", width: "180px", sortable: false, align: "center" }
      ],
      alert: {
        type: "warning",
        show: false,
        header: "",
        body: "",
        options: null
      }
    };
  },
  computed: {
    hasPendingItems() {
      return this.invoiceItems.some(item => !item.IsDelivered && this.canBeDelivered(item));
    }
  },
  watch: {
    invoiceId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.show = true;
          this.loadInvoiceDetails();
        }
      }
    }
  },
  methods: {
    async loadInvoiceDetails() {
      this.loading = true;
      try {
        // Obtener información de la factura
        const invoice = await this.$API.invoices.getInvoice(this.invoiceId);
        this.invoice = invoice;

        // Obtener productos de la factura con información de entrega
        const products = await this.$API.invoices.getInvoiceProductItemsWithDeliveryStatus(this.invoiceId);
        this.invoiceItems = products.map(item => ({
          ...item,
          WarehouseName: item.WarehouseName || 'Sin bodega'
        }));

      } catch (error) {
        console.error("Error al cargar detalles:", error);
        this.showAlert("danger", "Error", "No se pudieron cargar los detalles de la factura");
      } finally {
        this.loading = false;
      }
    },

    async markAsDelivered(item) {
      try {
        this.markingDelivery = true;
        const user = JSON.parse(localStorage.getItem("user"));
        
        await this.$API.invoices.markProductAsDelivered({
          ID_Invoice: this.invoice.ID_Invoice,
          ID_Item: item.ID_Item,
          Warehouse: item.Warehouse,
          ID_Business: this.invoice.ID_Business,
          ID_Branch: this.invoice.ID_Branch,
          ID_User: user.id
        });

        // Actualizar el item localmente
        item.IsDelivered = 1;
        item.DeliveryDate = new Date().toISOString();

        this.showAlert("success", "Éxito", `Producto "${item.ItemDescription}" marcado como entregado`);
        
        // Emitir evento para refrescar la lista principal
        this.$emit("deliveryUpdated");

      } catch (error) {
        console.error("Error al marcar como entregado:", error);
        this.showAlert("danger", "Error", "No se pudo marcar el producto como entregado");
      } finally {
        this.markingDelivery = false;
      }
    },

    async markAllAsDelivered() {
      try {
        this.markingDelivery = true;
        const user = JSON.parse(localStorage.getItem("user"));
        
        // Solo contar productos que se pueden entregar
        const pendingItems = this.invoiceItems.filter(item => !item.IsDelivered && this.canBeDelivered(item));
        
        if (pendingItems.length === 0) {
          this.showAlert("warning", "Aviso", "No hay productos pendientes que se puedan marcar como entregados");
          return;
        }
        
        // Obtener bodegas únicas de los productos pendientes
        const warehouses = [...new Set(pendingItems.map(item => item.Warehouse).filter(w => w))];
        
        await this.$API.invoices.markAllProductsAsDelivered({
          ID_Invoice: this.invoice.ID_Invoice,
          ID_Business: this.invoice.ID_Business,
          ID_Branch: this.invoice.ID_Branch,
          ID_User: user.id,
          Warehouses: warehouses
        });

        // Actualizar todos los items localmente (solo productos válidos)
        this.invoiceItems.forEach(item => {
          if (!item.IsDelivered && this.canBeDelivered(item)) {
            item.IsDelivered = 1;
            item.DeliveryDate = new Date().toISOString();
          }
        });

        this.showAlert("success", "Éxito", `${pendingItems.length} productos marcados como entregados`);
        
        // Emitir evento para refrescar la lista principal
        this.$emit("deliveryUpdated");

      } catch (error) {
        console.error("Error al marcar todos como entregados:", error);
        this.showAlert("danger", "Error", "No se pudieron marcar los productos como entregados");
      } finally {
        this.markingDelivery = false;
      }
    },

    async imprimirFacturaDirecta() {
      this.loading = true;
      try {
        await imprimirFactura(this.invoice.ID_Invoice, this);
      } catch (error) {
        console.error('Error al imprimir factura:', error);
        this.showAlert(
          "danger",
          "Error",
          "Ha ocurrido un error al imprimir la factura"
        );
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.show = false;
      this.$emit("close");
    },

    canBeDelivered(item) {
      // Los servicios no se pueden marcar como entregados
      if (item.IsService === 1 || item.ItemType === 'service') {
        return false;
      }
      // Los productos sin bodega no se pueden marcar
      if (!item.Warehouse) {
        return false;
      }
      // Si hay un filtro de bodega activo, solo se pueden marcar productos de esa bodega
      if (this.filteredWarehouse && item.Warehouse !== this.filteredWarehouse) {
        return false;
      }
      return true;
    },

    getDisabledReason(item) {
      if (item.IsService === 1 || item.ItemType === 'service') {
        return 'Los servicios no requieren entrega física';
      }
      if (!item.Warehouse) {
        return 'Producto sin bodega asignada';
      }
      if (this.filteredWarehouse && item.Warehouse !== this.filteredWarehouse) {
        return `Este producto es de otra bodega (${item.WarehouseName || 'Desconocida'})`;
      }
      return 'No se puede marcar como entregado';
    },

    getStatusColor(item) {
      if (item.IsDelivered) return 'success';
      if (item.IsService === 1 || item.ItemType === 'service') return 'grey';
      if (!item.Warehouse) return 'grey';
      return 'warning';
    },

    getStatusText(item) {
      if (item.IsDelivered) return 'Entregado';
      if (item.IsService === 1 || item.ItemType === 'service') return 'Servicio';
      if (!item.Warehouse) return 'Sin bodega';
      return 'Pendiente';
    },

    formatNumber(value) {
      if (!value && value !== 0) return "0.00";
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },

    showAlert(type, header, body, options = null) {
      this.alert.type = type;
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
      this.alert.options = options;
    },

    closeAlert() {
      this.alert.show = false;
    },

    acceptAlert() {
      this.alert.show = false;
    }
  }
};
</script>

<style scoped>
.info-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  height: 100%;
  min-height: 160px;
  display: flex;
  flex-direction: column;
}

.info-section h3 {
  margin-bottom: 12px;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.info-section p {
  margin-bottom: 8px;
  color: #666;
}

.table-scrollable {
  max-height: 400px;
}

.table-scrollable >>> .v-data-table__wrapper {
  overflow-y: auto;
  max-height: 400px;
}
</style>
